/**
 * Simulations List Component
 * Table of simulations with expandable content view using rich SimulationResultsContent
 */

import React, { useState } from 'react';
import { Play, ChevronDown, ChevronUp, Eye } from 'lucide-react';
import { SimulationPreview, USECASE_LABELS } from '../types';
import SimulationResultsContent from '@/features/simulationResults/SimulationResultsContent';

interface SimulationsListProps {
  simulations: SimulationPreview[];
}

const INITIAL_DISPLAY_COUNT = 3;

export const SimulationsList: React.FC<SimulationsListProps> = ({ simulations }) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [expandedSimulation, setExpandedSimulation] = useState<number | null>(null);
  const [showAll, setShowAll] = useState(false);

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const formatUseCase = (useCase: string | null): string => {
    if (!useCase) return 'N/A';
    return USECASE_LABELS[useCase] || useCase.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const handleViewContent = (simulationId: number) => {
    if (expandedSimulation === simulationId) {
      setExpandedSimulation(null);
      return;
    }
    setExpandedSimulation(simulationId);
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <Play className="w-5 h-5 text-gray-600" />
          <h3 className="text-lg font-semibold text-gray-900">Simulations</h3>
          <span className="px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-600 rounded-full">
            {simulations.length}
          </span>
        </div>
        {isExpanded ? (
          <ChevronUp className="w-5 h-5 text-gray-400" />
        ) : (
          <ChevronDown className="w-5 h-5 text-gray-400" />
        )}
      </button>

      {isExpanded && (
        <div className="px-6 pb-6">
          {simulations.length === 0 ? (
            <div className="text-center text-gray-500 py-8">
              No simulations run in this time period
            </div>
          ) : (
            <>
              <div className="space-y-3">
                {(showAll ? simulations : simulations.slice(0, INITIAL_DISPLAY_COUNT)).map((simulation) => (
                  <div key={simulation.id}>
                    <div
                      className={`p-4 rounded-lg border transition-colors ${
                        expandedSimulation === simulation.id
                          ? 'border-blue-200 bg-blue-50'
                          : 'border-gray-100 hover:border-gray-200'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h4 className="font-medium text-gray-900">{simulation.name}</h4>
                          <div className="flex items-center gap-3 mt-2">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                              {formatUseCase(simulation.use_case)}
                            </span>
                            <span className="text-xs text-gray-400">
                              {formatDate(simulation.created_at)}
                            </span>
                          </div>
                          {simulation.goal_of_simulation && (
                            <p className="text-sm text-gray-600 mt-2 line-clamp-2">
                              <span className="font-medium">Goal:</span> {simulation.goal_of_simulation}
                            </p>
                          )}
                          {simulation.response_preview && (
                            <p className="text-sm text-gray-500 mt-1 line-clamp-2">
                              {simulation.response_preview}...
                            </p>
                          )}
                        </div>
                        <button
                          onClick={() => handleViewContent(simulation.id)}
                          className={`ml-4 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors flex items-center gap-1 ${
                            expandedSimulation === simulation.id
                              ? 'bg-blue-600 text-white'
                              : 'text-blue-600 hover:bg-blue-50'
                          }`}
                        >
                          <Eye className="w-4 h-4" />
                          {expandedSimulation === simulation.id ? 'Hide' : 'View'}
                        </button>
                      </div>
                    </div>

                    {/* Expanded Content - Rich Simulation Results */}
                    {expandedSimulation === simulation.id && (
                      <div className="mt-2 border border-gray-200 rounded-lg overflow-hidden">
                        <SimulationResultsContent
                          simulationId={simulation.id}
                          isAdminView={true}
                          defaultChatOpen={false}
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* See More / Show Less */}
              {simulations.length > INITIAL_DISPLAY_COUNT && (
                <div className="mt-3">
                  <button
                    onClick={() => setShowAll(!showAll)}
                    className="w-full py-2 text-sm font-medium text-blue-600 hover:text-blue-800 transition-colors"
                  >
                    {showAll ? `Show Less` : `See More (${simulations.length - INITIAL_DISPLAY_COUNT} more simulations)`}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default SimulationsList;
